import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import api, { billingSubscription, formatApiErrorDetail } from "../api";
import { useAuth } from "../AuthContext";
import { Shield, Trophy, Mail, Check, X, Sparkles, RefreshCw, Lock, Flame, Swords, Copy, Pencil, Monitor, Gamepad2, Settings, EyeOff, Coins, ArrowRight, Clock, CheckCircle2 } from "lucide-react";
import { FaTwitch, FaYoutube, FaTiktok, FaInstagram, FaXTwitter, FaDiscord } from "react-icons/fa6";
import { FaPenToSquare } from "react-icons/fa6";
import { SiKick } from "react-icons/si";
import { toast } from "sonner";

const ACT_COOLDOWN_DAYS = 14;
const PROFILE_DRAFT_FIELDS = [
  "act",
  "gaming_platform",
  "discord_username",
  "twitch_url",
  "kick_url",
  "youtube_url",
  "tiktok_url",
  "instagram_link",
  "x_link",
  "accent_color",
  "avatar",
  "banner",
  "hidden_achievements",
];

function toProfileDraft(u) {
  if (!u) return null;
  return {
    act: u.act || "",
    gaming_platform: u.gaming_platform || "pc",
    discord_username: u.discord_username || "",
    twitch_url: u.twitch_url || "",
    kick_url: u.kick_url || "",
    youtube_url: u.youtube_url || "",
    tiktok_url: u.tiktok_url || "",
    instagram_link: u.instagram_link || "",
    x_link: u.x_link || "",
    accent_color: u.accent_color || "#FFCC00",
    avatar: u.avatar || null,
    banner: u.banner || null,
    hidden_achievements: u.hidden_achievements || [],
  };
}

function isProfileDraftDirty(user, draft) {
  if (!user || !draft) return false;
  const base = toProfileDraft(user);
  return PROFILE_DRAFT_FIELDS.some((k) => {
    if (Array.isArray(draft?.[k])) {
      return JSON.stringify(draft[k]) !== JSON.stringify(base[k]);
    }
    return (draft?.[k] ?? null) !== (base?.[k] ?? null);
  });
}

function formatActCooldown(changedAt) {
  if (!changedAt) return null;
  try {
    const next = new Date(new Date(changedAt).getTime() + ACT_COOLDOWN_DAYS * 86400000);
    const diff = next - new Date();
    if (diff <= 0) return null;
    const days = Math.floor(diff / 86400000);
    const hrs = Math.floor((diff % 86400000) / 3600000);
    return `${days} يوم و${hrs} ساعة`;
  } catch {
    return null;
  }
}

export default function ProfilePage() {
  const { user, refresh } = useAuth();
  const [invites, setInvites] = useState([]);
  const [draft, setDraft] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [archivedClan, setArchivedClan] = useState(null);
  const [billingStatus, setBillingStatus] = useState(null);
  const [referralStats, setReferralStats] = useState(null);
  const [copiedReferral, setCopiedReferral] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/me/invites");
      setInvites(data);
    } catch (err) {
      setInvites([]);
      if (err?.response?.status !== 401) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
      }
    }
  };

  useEffect(() => {
    if (!user) return;
    load();
  }, [user]);

  useEffect(() => {
    if (user) {
      setDraft(toProfileDraft(user));
      api.get("/me/archived-clan").then((r) => setArchivedClan(r.data)).catch(() => setArchivedClan(null));
      billingSubscription().then((r) => setBillingStatus(r.data)).catch(() => setBillingStatus(null));
      api.get("/me/referrals").then((r) => setReferralStats(r.data)).catch(() => setReferralStats(null));

    }
  }, [user]);

  const restoreClan = async () => {
    if (!archivedClan) return;
    if (!confirm(`استعادة كلان "${archivedClan.name}" من الأرشيف؟`)) return;
    try {
      await api.post(`/clans/${archivedClan.id}/restore`);
      toast.success("تم استعادة الكلان بنجاح");
      await refresh();
      setArchivedClan(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const respond = async (inv, action) => {
    try {
      const { data } = await api.post(`/invites/${inv.id}`, { action });
      if (data?.reward_granted) {
        toast.success("🎁 امتلأ كلانك! حصلت على Plus 7 أيام!", { duration: 6000 });
      } else {
        toast.success(action === "accept" ? "انضممت للكلان" : "رفضت الدعوة");
      }
      await refresh();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const copyReferralLink = async () => {
    const link = referralStats?.referral_link;
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopiedReferral(true);
      toast.success("تم نسخ رابط الإحالة بنجاح!");
      setTimeout(() => setCopiedReferral(false), 2000);
    } catch {
      toast.error("تعذر نسخ الرابط");
    }
  };

  const onFieldChange = (key, value) => {
    setDraft((prev) => ({ ...(prev || {}), [key]: value }));
  };

  const discardDraft = () => {
    if (!user) return;
    setDraft(toProfileDraft(user));
    toast.success("تم تجاهل التعديلات");
  };

  const saveDraft = async () => {
    if (!user || !draft || savingDraft) return;
    const base = toProfileDraft(user);
    const payload = {};
    for (const k of PROFILE_DRAFT_FIELDS) {
      if (Array.isArray(draft?.[k])) {
        if (JSON.stringify(draft[k]) !== JSON.stringify(base[k])) {
          payload[k] = draft[k];
        }
      } else if ((draft?.[k] ?? null) !== (base?.[k] ?? null)) {
        payload[k] = draft?.[k];
      }
    }
    if (Object.keys(payload).length === 0) return;
    setSavingDraft(true);
    try {
      const { data } = await api.put("/me/profile", payload);
      setDraft(toProfileDraft(data));
      await refresh();
      toast.success("✅ تم حفظ التغييرات بنجاح");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingDraft(false);
    }
  };

  if (!user) return null;

  const profileView = { ...user, ...(draft || {}) };
  const isPlus = !!user.is_personal_plus;
  const accent = (isPlus && profileView.accent_color) || "#FFCC00";
  const bannerStyle = isPlus && profileView.banner
    ? { backgroundImage: `url(${profileView.banner})`, backgroundSize: "cover", backgroundPosition: "center" }
    : { backgroundColor: accent };
  const hasDraftChanges = isProfileDraftDirty(user, draft);

  return (
    <div className="space-y-8 w-full max-w-full overflow-hidden" dir="rtl">
      <PremiumProfileHeader
        user={profileView}
        rawUser={user}
        draft={draft}
        accent={accent}
        isPlus={isPlus}
        bannerStyle={bannerStyle}
        onFieldChange={onFieldChange}
      />

      <WeeklyChallengesSection />

      {user.clan_id && (
        <Link to={`/clans/${user.clan_id}`} data-testid="profile-clan-link" className="block bg-[#111113]/90 border border-white/10 rounded-2xl p-5 hover:border-gold-500/40 transition shadow-xl">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gold-500/10 border border-gold-500/30 text-gold-500 grid place-items-center shrink-0">
              <Shield size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase font-bold tracking-widest text-white/40">الكلان الخاص بك</div>
              <div className="font-display font-black text-base md:text-lg text-white truncate">انتقل إلى صفحة الكلان لإدارة الفريق 🛡️</div>
            </div>
            <ArrowRight size={18} className="text-white/40 shrink-0" />
          </div>
        </Link>
      )}

      <section className="bg-[#111113]/90 border border-white/10 rounded-3xl p-5 md:p-8 shadow-2xl space-y-5" data-testid="referrals-rewards-card">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 grid place-items-center shrink-0">
            <Coins size={20} />
          </div>
          <div className="min-w-0">
            <h2 className="font-display font-black text-lg md:text-xl text-white truncate">نظام الإحالات ومكافآت RIV</h2>
            <p className="text-xs text-white/50">ادع أصدقائك واجمع النقاط للحصول على اشتراكات Plus مجانية</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="rounded-2xl border border-white/10 bg-black/40 p-4 shadow-inner">
            <div className="text-[10px] uppercase font-bold tracking-widest text-white/40">رصيدك الحالي</div>
            <div className="font-display font-black text-xl md:text-2xl text-emerald-400 mt-1">🪙 {Number(referralStats?.riv_points ?? user?.riv_points ?? 0)} RIV</div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-black/40 p-4 shadow-inner">
            <div className="text-[10px] uppercase font-bold tracking-widest text-white/40">عدد المدعوين</div>
            <div className="font-display font-black text-xl md:text-2xl text-gold-400 mt-1">{Number(referralStats?.invited_count || 0)} لاعب</div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-black/40 p-4 shadow-inner">
            <div className="text-[10px] uppercase font-bold tracking-widest text-white/40">مكافأة كل دعوة</div>
            <div className="font-display font-black text-xl md:text-2xl text-emerald-400 mt-1">+{Number(referralStats?.reward_per_invite || 1)} RIV</div>
          </div>
        </div>

        <div>
          <label className="text-xs font-bold text-white/70 mb-2 block">رابط الإحالة الخاص بك</label>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              readOnly
              value={referralStats?.referral_link || ""}
              className="flex-1 min-w-[200px] bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-xs font-mono text-white/80 outline-none truncate"
            />
            <button
              type="button"
              onClick={copyReferralLink}
              className={`w-full sm:w-auto px-5 py-3 rounded-xl font-black text-xs inline-flex items-center justify-center gap-2 transition-all duration-300 ${copiedReferral ? "bg-emerald-400 text-black shadow-[0_0_20px_rgba(16,185,129,0.5)]" : "bg-emerald-500 text-black hover:bg-emerald-400 shadow-md"}`}
            >
              <Copy size={14} /> {copiedReferral ? "تم النسخ بنجاح! ✓" : "نسخ الرابط"}
            </button>
          </div>
        </div>

        <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-4 text-xs text-white/75 leading-relaxed space-y-1">
          <div className="font-bold text-emerald-300">💡 كيف تستفيد من نقاط RIV؟</div>
          <p>
            كل نقطة RIV تساوي ريالاً واحداً! امنح أصدقائك رابط الدعوة الخاص بك، وستحصل على نقطة فورية عند تسجيلهم وتأكيد بريدهم الإلكتروني. يمكنك استبدال النقاط لاحقاً لتجديد اشتراكات Plus مجاناً بالكامل (11 نقطة لـ Personal Plus أو 27 نقطة لـ Clan Plus).
          </p>
        </div>
      </section>

      <div className={`rounded-3xl p-5 md:p-8 border shadow-xl ${user.is_plus ? "border-gold-500/40 bg-gradient-to-b from-gold-500/10 via-[#111113]/90 to-[#111113]/90" : "border-white/10 bg-[#111113]/90"}`}>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-3">
          <div className="h-10 w-10 rounded-xl bg-gold-500/10 border border-gold-500/30 text-gold-500 grid place-items-center shrink-0">
            <Sparkles size={20} />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="font-display font-black text-lg md:text-xl text-white truncate">RIVALS Plus (إدارة الكلان)</h2>
            <p className="text-xs text-white/50">ترقية قدرات وميزات فريقك التنافسي</p>
          </div>
          {user.is_plus && <span className="text-[10px] font-black uppercase tracking-widest bg-gold-500 text-black px-2.5 py-1 rounded-lg">مفعل</span>}
        </div>

        {user.plus_expires_at && (
          <div className="text-xs font-bold text-gold-400 mb-3 bg-gold-500/10 border border-gold-500/20 px-3 py-1.5 rounded-xl inline-block" data-testid="plus-expiry">
            ينتهي الاشتراك خلال: {Math.max(0, Math.ceil((new Date(user.plus_expires_at) - new Date()) / 86400000))} يوم
          </div>
        )}
        
        {billingStatus && (
          <div className="text-xs text-white/50 mb-3 font-mono truncate" data-testid="billing-source">
            مصدر الاشتراك: {billingStatus.sources?.includes("manual") ? "إداري يدوي" : "—"}
            {billingStatus.sources?.includes("manual") && billingStatus.sources?.includes("billing") ? " + " : ""}
            {billingStatus.sources?.includes("billing") ? "بوابة الدفع" : ""}
          </div>
        )}

        <ul className="text-xs text-white/70 space-y-2 mb-5 list-disc pr-5">
          <li>زيادة سعة أعضاء الكلان من 7 إلى 12 لاعب</li>
          <li>تعيين نائبَين لقائد الكلان بدل نائب واحد</li>
          <li>أولوية الاستجابة والدعم في نزاعات المباريات</li>
          <li className="text-gold-400 font-bold">🎁 عند اكتمال الكلان بـ 6 لاعبين، تحصل على Plus مجاناً لمدة 7 أيام!</li>
        </ul>

        <Link to="/plus" data-testid="subscriptions-btn" className="inline-block px-5 py-2.5 rounded-xl bg-gold-500 text-black font-bold text-xs">
          إدارة الاشتراكات
        </Link>
      </div>

      {archivedClan && !user.clan_id && (
        <div data-testid="restore-clan-card" className="bg-[#111113]/90 border border-gold-500/30 rounded-2xl p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 shadow-xl">
          <div className="h-10 w-10 rounded-xl bg-gold-500/10 border border-gold-500/30 text-gold-500 grid place-items-center shrink-0">
            <Shield size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-black text-base text-white truncate">كلانك في الأرشيف: {archivedClan.name}</div>
            <div className="text-xs text-white/50 mt-0.5">[{archivedClan.tag}] • مؤرشف منذ {new Date(archivedClan.archived_at).toLocaleDateString("ar")}</div>
          </div>
          <button
            data-testid="restore-clan-btn"
            onClick={restoreClan}
            className="w-full sm:w-auto px-4 py-2.5 rounded-xl bg-gold-500 text-black font-black hover:bg-gold-400 flex items-center justify-center gap-2 text-xs shadow-md transition"
          >
            <RefreshCw size={14} /> استعادة الكلان من الأرشيف
          </button>
        </div>
      )}

      {user.clan_cooldown_until && new Date(user.clan_cooldown_until) > new Date() && (
        <div data-testid="cooldown-banner" className="bg-destructive/10 border border-destructive/30 rounded-2xl p-5 flex items-start sm:items-center gap-3 shadow-xl">
          <X className="text-destructive shrink-0 mt-0.5 sm:mt-0" size={20} />
          <div className="flex-1 min-w-0">
            <div className="font-bold text-destructive text-sm">أنت في فترة حظر مؤقت (Cooldown)</div>
            <div className="text-xs text-white/70 mt-1">
              لا يمكنك الانضمام لكلان جديد قبل تاريخ: {new Date(user.clan_cooldown_until).toLocaleString("ar")}
            </div>
          </div>
        </div>
      )}

      <section className="bg-[#111113]/90 border border-white/10 rounded-3xl p-5 md:p-6 shadow-xl">
        <h2 className="font-display font-black text-lg md:text-xl mb-4 flex items-center gap-2 text-white">
          <Mail size={20} className="text-gold-500" /> دعوات الانضمام الواردة ({invites.length})
        </h2>
        {invites.length === 0 ? (
          <div className="text-center text-white/40 py-8 text-xs">لا توجد دعوات انضمام معلقة حالياً</div>
        ) : (
          <div className="space-y-2.5">
            {invites.map((inv) => (
              <div key={inv.id} className="bg-black/40 border border-white/5 rounded-2xl p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 shadow-inner" data-testid={`invite-${inv.id}`}>
                <div className="flex items-center gap-3 w-full sm:w-auto">
                  <div className="h-10 w-10 rounded-xl bg-gold-500/10 border border-gold-500/20 text-gold-500 grid place-items-center shrink-0">
                    <Shield size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-white text-sm truncate">{inv.clan_name}</div>
                    <div className="text-xs text-white/40 font-mono">[{inv.clan_tag}]</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
                  <button data-testid={`invite-accept-${inv.id}`} onClick={() => respond(inv, "accept")} className="flex-1 sm:flex-initial px-3.5 py-2 rounded-xl bg-gold-500 text-black font-black hover:bg-gold-400 text-xs shadow-md transition flex items-center justify-center gap-1">
                    <Check size={14} /> قبول
                  </button>
                  <button onClick={() => respond(inv, "reject")} className="flex-1 sm:flex-initial px-3.5 py-2 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 text-destructive text-xs font-bold transition text-center">
                    رفض
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <ProfileDraftActionBar
        visible={hasDraftChanges}
        saving={savingDraft}
        onSave={saveDraft}
        onDiscard={discardDraft}
      />
    </div>
  );
}

function WeeklyChallengesSection() {
  const [challengesData, setChallengesData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchChallenges();
  }, []);

  const fetchChallenges = async () => {
    try {
      const { data } = await api.get("/me/challenges");
      setChallengesData(data);
    } catch (err) {
      // تجاهل الخطأ
    } finally {
      setLoading(false);
    }
  };

  if (loading) return null;

  return (
    <section className="bg-[#111113]/90 border border-gold-500/20 rounded-3xl p-5 md:p-8 shadow-2xl relative overflow-hidden space-y-6">
      <div className="absolute top-0 left-0 w-72 h-72 bg-gold-500/5 rounded-full blur-3xl pointer-events-none" />

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-white/10 pb-5">
        <div className="space-y-1">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gold-500/10 border border-gold-500/30 text-gold-400 text-[10px] font-bold tracking-widest uppercase">
            <Sparkles size={12} /> التحديات الأسبوعية
          </div>
          <h2 className="font-display font-black text-lg md:text-2xl text-white">أنجز التحديات واكسب نقاط RIV 🔥</h2>
          <p className="text-xs text-white/50">تتحقق الإدارة من الإنجاز وتعتمد مكافآت التحديات الأسبوعية. مكافآت الإحالة تستخدم نظام الدعوات.</p>
        </div>

        <div className="px-4 py-2 rounded-2xl bg-black/40 border border-white/10 text-xs text-white/70 flex items-center gap-2 self-start md:self-auto">
          <Clock size={14} className="text-gold-400" /> الأسبوع الحالي: <span className="font-mono text-gold-400 font-bold">{challengesData?.week_key}</span>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {challengesData?.challenges?.map((ch) => {
          const textMatch = ch.desc?.match(/(\d+)/) || ch.title?.match(/(\d+)/);
          const targetNum = textMatch ? parseInt(textMatch[1], 10) : 1;
          const currentProgress = ch.completed ? targetNum : 0;
          const percent = (currentProgress / targetNum) * 100;

          return (
            <div
              key={ch.id}
              className={`rounded-2xl p-5 border transition-all duration-300 flex flex-col justify-between space-y-4 ${
                ch.completed
                  ? "bg-emerald-500/5 border-emerald-500/30 shadow-[0_0_15px_rgba(16,185,129,0.03)]"
                  : "bg-black/40 border-white/10 hover:border-gold-500/30"
              }`}
            >
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-display font-bold text-sm md:text-base text-white flex items-center gap-2">
                    <Trophy size={16} className={ch.completed ? "text-emerald-400" : "text-gold-400"} />
                    {ch.title}
                  </h3>
                  <span className="px-2.5 py-1 rounded-full bg-gold-500/10 border border-gold-500/30 text-gold-400 text-xs font-mono font-bold flex items-center gap-1 shrink-0">
                    <Coins size={12} /> +{ch.reward} RIV
                  </span>
                </div>
                <p className="text-xs text-white/60 leading-relaxed">{ch.desc}</p>
              </div>

              <div className="space-y-1.5 pt-2 border-t border-white/5">
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-white/40">التقدم:</span>
                  <span className={`font-mono font-bold ${ch.completed ? "text-emerald-400" : "text-gold-400"}`} dir="ltr">
                    {currentProgress} / {targetNum} ({ch.completed ? "مكتمل" : "قيد التنفيذ"})
                  </span>
                </div>
                <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all duration-500 rounded-full ${
                      ch.completed ? "bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]" : "bg-gold-500"
                    }`}
                    style={{ width: `${percent}%` }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function PremiumProfileHeader({ user, rawUser, draft, accent, isPlus, bannerStyle, onFieldChange }) {
  const role = user.role === "owner" ? "مالك المنصة" : user.role === "admin" ? "منظم رسمي" : "لاعب محترف";
  const roleBadgeClass = user.role === "owner"
    ? "text-gold-400 border-gold-500/40 bg-gold-500/10"
    : user.role === "admin"
      ? "text-purple-300 border-purple-500/30 bg-purple-500/10"
      : "text-white/80 border-white/20 bg-black/40";
  const initial = (user.username?.[0] || "?").toUpperCase();
  const avatarInputRef = useRef(null);
  const bannerInputRef = useRef(null);
  const [editingAct, setEditingAct] = useState(false);
  const [isEditingBadges, setIsEditingBadges] = useState(false);
  const [filterBadgesMode, setFilterBadgesMode] = useState("unlocked");

  const platform = (user.gaming_platform || "pc").toLowerCase();
  const hiddenBadges = draft?.hidden_achievements || [];

  const dynamicBadges = (user.achievements || []).map((badge) => ({
    id: badge.id || badge.tournament_id,
    icon: <Trophy size={14} />,
    label: badge.label || badge.title || "بطل بطولة",
    value: "🏆",
    tone: badge.kind === "tournament_gold" ? "gold" : "silver",
    isDynamic: true,
  }));

  const staticBadges = [
    { id: "attendance", icon: <Check size={14} />, label: "عدد مرات التحضير", value: user.attendances || 0, tone: "silver" },
    { id: "mvp", icon: <Trophy size={14} />, label: "نجم المباراة", value: user.mvp_count || 0, tone: "gold" },
    { id: "wins", icon: <Swords size={14} />, label: "Wins", value: user.wins || 0, tone: "bronze" },
    { id: "losses", icon: <Flame size={14} />, label: "Losses", value: user.losses || 0, tone: "danger" },
  ];

  const ALL_ACHIEVEMENTS = [...dynamicBadges, ...staticBadges];

  const toggleHideBadge = (badgeId) => {
    let nextHidden;
    if (hiddenBadges.includes(badgeId)) {
      nextHidden = hiddenBadges.filter((item) => item !== badgeId);
    } else {
      nextHidden = [...hiddenBadges, badgeId];
    }
    onFieldChange("hidden_achievements", nextHidden);
  };

  const displayedAchievements = ALL_ACHIEVEMENTS.filter((badge) => {
    if (!isEditingBadges && hiddenBadges.includes(badge.id)) return false;
    if (filterBadgesMode === "unlocked") {
      return badge.isDynamic ? true : Number(badge.value) > 0;
    }
    return true;
  });

  const pickVisual = (kind) => {
    if (!isPlus) {
      toast.error("التخصيص البصري متاح لمشتركي Personal Plus فقط");
      return;
    }
    if (kind === "avatar") avatarInputRef.current?.click();
    else bannerInputRef.current?.click();
  };

  const onPickImage = async (file, type) => {
    if (!file) return;
    const max = type === "avatar" ? 2_000_000 : 3_000_000;
    if (file.size > max) {
      toast.error(type === "avatar" ? "الأفاتار كبير (الحد 2MB)" : "البانر كبير (الحد 3MB)");
      return;
    }
    const dataUrl = await fileToDataUrl(file);
    onFieldChange(type, dataUrl);
  };

  return (
    <div data-testid="premium-profile" className="rounded-3xl overflow-hidden border border-white/10 bg-[#111113]/90 shadow-2xl">
      <div data-testid="profile-banner" className="h-40 md:h-56 w-full relative group/banner cursor-pointer" style={bannerStyle} onClick={() => pickVisual("banner")}>
        <div className="absolute inset-0 bg-gradient-to-t from-[#111113] via-[#111113]/40 to-transparent" />
        <MediaEditOverlay label="تعديل البنر والخلفية" />
        <input
          ref={bannerInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => onPickImage(e.target.files?.[0], "banner")}
        />
        <div className="absolute bottom-3 right-3 flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase font-bold tracking-widest backdrop-blur-md px-3 py-1 rounded-full border ${roleBadgeClass}`}>
            <Shield size={11} /> {role}
          </span>
          {isPlus && (
            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase font-black tracking-widest px-3 py-1 rounded-full border border-gold-500/40 bg-gold-500/20 text-gold-400 shadow-md">
              <Sparkles size={11} className="text-gold-400" /> Personal Plus
            </span>
          )}
        </div>
      </div>

      <div className="px-5 md:px-8 pb-6 -mt-12 md:-mt-14 relative space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 items-end">
          <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4 min-w-0">
            <div
              data-testid="profile-avatar"
              onClick={() => pickVisual("avatar")}
              className="h-24 w-24 md:h-28 md:w-28 rounded-full border-4 border-[#111113] grid place-items-center overflow-hidden text-3xl font-display font-black relative group/avatar cursor-pointer shrink-0 shadow-2xl transition-transform duration-300 hover:scale-105"
              style={{
                backgroundColor: isPlus && user.avatar ? "transparent" : `${accent}22`,
                color: accent,
                boxShadow: `0 0 0 3px ${accent}55, 0 10px 25px -5px rgba(0, 0, 0, 0.5)`,
              }}
            >
              {isPlus && user.avatar ? (
                <img src={user.avatar} alt={user.username} className="h-full w-full object-cover" />
              ) : initial}
              <MediaEditOverlay avatar label="تعديل" />
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => onPickImage(e.target.files?.[0], "avatar")}
              />
            </div>
            <div className="flex-1 min-w-0 space-y-1 w-full">
              {!editingAct ? (
                <div className="flex items-center gap-2.5 flex-wrap">
                  {user.act ? (
                    <h1 data-testid="profile-act-display" className="font-display font-black text-xl sm:text-2xl md:text-3xl leading-tight break-all whitespace-normal" style={{ color: accent }}>
                      {user.act}
                    </h1>
                  ) : (
                    <h1 className="font-display font-black text-xl sm:text-2xl md:text-3xl leading-tight break-all whitespace-normal text-white">{user.username}</h1>
                  )}

                  {platform === "ps5" && (
                    <span className="text-xs px-2.5 py-0.5 rounded-lg bg-[#003791]/30 text-[#0070d1] border border-[#003791]/50 font-bold flex items-center gap-1 shrink-0">
                      <Gamepad2 size={12} /> PS5
                    </span>
                  )}
                  {platform === "xbox" && (
                    <span className="text-xs px-2.5 py-0.5 rounded-lg bg-[#107c41]/30 text-[#107c41] border border-[#107c41]/50 font-bold flex items-center gap-1 shrink-0">
                      <Gamepad2 size={12} /> Xbox
                    </span>
                  )}
                  {(platform === "pc" || !platform) && (
                    <span className="text-xs px-2.5 py-0.5 rounded-lg bg-purple-500/20 text-purple-300 border border-purple-500/40 font-bold flex items-center gap-1 shrink-0">
                      <Monitor size={12} /> PC
                    </span>
                  )}

                  <button
                    type="button"
                    disabled={!!formatActCooldown(rawUser?.act_changed_at)}
                    onClick={() => setEditingAct(true)}
                    className="p-1.5 rounded-lg border border-white/10 text-white/70 hover:bg-white/10 disabled:opacity-40 transition shrink-0"
                    title="تعديل حساب اللعب"
                  >
                    <Pencil size={13} />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2 w-full pt-1">
                  <input
                    data-testid="profile-act-input"
                    value={draft?.act || ""}
                    onChange={(e) => onFieldChange("act", e.target.value)}
                    minLength={2}
                    maxLength={40}
                    placeholder="YourName#1234"
                    className="flex-1 bg-black/40 border border-white/10 rounded-xl px-3.5 py-2 outline-none focus:border-gold-500/50 text-xs text-white min-w-0"
                  />
                  <button type="button" onClick={() => setEditingAct(false)} className="px-3.5 py-2 rounded-xl bg-gold-500 text-black font-bold text-xs shrink-0">
                    تم
                  </button>
                </div>
              )}
              
              <div className="text-white/50 text-xs font-medium truncate">@{user.username} • {user.email}</div>
              {formatActCooldown(rawUser?.act_changed_at) ? (
                <div data-testid="act-cooldown-msg" className="text-[11px] text-destructive mt-1 inline-flex items-center gap-1 font-bold">
                  <Lock size={12} /> لا يمكنك تعديل حساب اللعب إلا بعد {formatActCooldown(rawUser?.act_changed_at)}
                </div>
              ) : null}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 w-full lg:w-[460px] bg-black/40 border border-white/10 rounded-2xl p-3 shadow-inner" data-testid="profile-main-stats">
            <Stat label="نقاط" value={user.points || 0} accent={accent} testid="stat-points" />
            <Stat label="فوز" value={user.wins || 0} accent={accent} testid="stat-wins" />
            <Stat label="خسارة" value={user.losses || 0} accent={accent} testid="stat-losses" />
            <Stat label="W/L" value={user.kd ?? 0} accent={accent} testid="stat-kd" />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-5 pt-2">
          <div className="bg-black/30 p-4 sm:p-5 rounded-2xl border border-white/10 shadow-inner space-y-3">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
              <div className="text-[10px] uppercase font-bold tracking-widest text-white/40">الإنجازات والأوسمة المكتسبة</div>

              <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto justify-between sm:justify-end">
                <div className="flex bg-black/50 p-0.5 rounded-xl border border-white/10 text-xs font-bold">
                  <button
                    type="button"
                    onClick={() => setFilterBadgesMode("unlocked")}
                    className={`px-3 py-1 rounded-lg transition ${filterBadgesMode === "unlocked" ? "bg-white/10 text-white" : "text-white/40 hover:text-white"}`}
                  >
                    المحققة
                  </button>
                  <button
                    type="button"
                    onClick={() => setFilterBadgesMode("all")}
                    className={`px-3 py-1 rounded-lg transition ${filterBadgesMode === "all" ? "bg-white/10 text-white" : "text-white/40 hover:text-white"}`}
                  >
                    الكل
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => setIsEditingBadges(!isEditingBadges)}
                  className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-xl font-bold transition shadow-sm ${
                    isEditingBadges ? "bg-emerald-600 text-white" : "bg-white/10 hover:bg-white/20 text-white/80"
                  }`}
                >
                  <Settings size={13} /> {isEditingBadges ? "إنهاء التخصيص" : "تخصيص العرض"}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5" data-testid="profile-achievements">
              {displayedAchievements.length === 0 ? (
                <div className="col-span-full text-center py-8 text-xs text-white/30">لا توجد إنجازات معروضة حالياً</div>
              ) : (
                displayedAchievements.map((badge) => {
                  const isHidden = hiddenBadges.includes(badge.id);
                  return (
                    <AchievementBadge
                      key={badge.id}
                      icon={badge.icon}
                      label={badge.label}
                      value={badge.value}
                      accent={accent}
                      tone={badge.tone}
                      isEditing={isEditingBadges}
                      isHidden={isHidden}
                      onToggleHide={() => toggleHideBadge(badge.id)}
                    />
                  );
                })
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div className="rounded-2xl border border-white/10 bg-black/30 p-4 shadow-inner">
              <div className="text-[10px] uppercase font-bold tracking-widest text-white/45 mb-2 block">
                منصة اللعب الأساسية (Gaming Platform)
              </div>
              <select
                value={draft?.gaming_platform || "pc"}
                onChange={(e) => onFieldChange("gaming_platform", e.target.value)}
                className="w-full bg-black/40 border border-white/10 rounded-xl px-3.5 py-2.5 outline-none text-xs font-bold text-white focus:border-gold-500/50"
              >
                <option value="pc">🖥️ PC (كمبيوتر - يتطلب RivalsGuard)</option>
                <option value="ps5">🎮 PlayStation 5</option>
                <option value="xbox">🎮 Xbox Series X/S</option>
              </select>
              <p className="text-[10px] text-white/40 mt-1.5 leading-relaxed">
                تأكد من اختيار منصتك الحقيقية لتفعيل شارة وميزات حماية RivalsGuard في غرف المباريات.
              </p>
            </div>

            <SingleColorPicker value={draft?.accent_color || "#FFCC00"} onChange={(v) => onFieldChange("accent_color", v)} disabled={!isPlus} />
            
            <div className="text-[10px] uppercase font-bold tracking-widest text-white/40">القنوات والحسابات الاجتماعية</div>
            <SocialChannelsCard user={user} onFieldChange={onFieldChange} testidPrefix="social" />
          </div>
        </div>
      </div>
    </div>
  );
}

const SOCIAL_PLATFORMS = [
  { key: "discord_username", label: "Discord", testid: "social-discord", Icon: FaDiscord, color: "#5865F2", kind: "text", placeholder: "your_username" },
  { key: "twitch_url", label: "Twitch", testid: "social-twitch", Icon: FaTwitch, color: "#a970ff" },
  { key: "kick_url", label: "Kick", testid: "social-kick", Icon: SiKick, color: "#53fc18" },
  { key: "youtube_url", label: "YouTube", testid: "social-youtube", Icon: FaYoutube, color: "#ff0000" },
  { key: "tiktok_url", label: "TikTok", testid: "social-tiktok", Icon: FaTiktok, color: "#ffffff" },
  { key: "instagram_link", label: "Instagram", testid: "social-instagram", Icon: FaInstagram, color: "#e1306c" },
  { key: "x_link", label: "X", testid: "social-x", Icon: FaXTwitter, color: "#ffffff" },
];

function SocialChannelsCard({ user, onFieldChange, testidPrefix = "social" }) {
  const [editingKey, setEditingKey] = useState("");

  const startEdit = (key) => {
    setEditingKey(key);
  };

  const cancelEdit = () => {
    setEditingKey("");
  };

  return (
    <div className="grid gap-2">
      {SOCIAL_PLATFORMS.map((p) => {
        const url = user?.[p.key] || "";
        return (
          <SocialRow
            key={p.key}
            platform={{ ...p, testid: `${testidPrefix}-${p.label.toLowerCase()}` }}
            url={url}
            isEditing={editingKey === p.key}
            onDraftChange={(v) => onFieldChange?.(p.key, v)}
            onEdit={() => startEdit(p.key)}
            onCancel={cancelEdit}
            onSave={() => setEditingKey("")}
          />
        );
      })}
    </div>
  );
}

function AchievementBadge({ icon, label, value, accent, tone = "gold", isEditing, isHidden, onToggleHide }) {
  const toneClass = tone === "gold"
    ? "border-gold-500/30 bg-gold-500/10"
    : tone === "silver"
      ? "border-gray-300/30 bg-gray-300/10"
      : tone === "bronze"
        ? "border-amber-600/40 bg-amber-600/10"
        : "border-destructive/35 bg-destructive/10";
  const iconClass = tone === "gold"
    ? "text-gold-400"
    : tone === "silver"
      ? "text-gray-300"
      : tone === "bronze"
        ? "text-amber-500"
        : "text-destructive";
  return (
    <div className={`relative rounded-2xl border p-3 text-center transition-all shadow-sm ${toneClass} ${
      isHidden && isEditing ? "opacity-30 border-dashed border-red-500/60" : ""
    }`}>
      {isEditing && (
        <button
          type="button"
          onClick={onToggleHide}
          className={`absolute -top-2 -right-2 p-1.5 rounded-full text-[10px] shadow-lg transition ${
            isHidden ? "bg-red-600 text-white" : "bg-black/85 text-gray-300 hover:text-white border border-white/20"
          }`}
          title={isHidden ? "إظهار الإنجاز" : "إخفاء الإنجاز"}
        >
          <EyeOff size={12} />
        </button>
      )}
      <div className="mb-1.5 inline-flex items-center justify-center h-7 w-7 rounded-xl bg-black/40 border border-white/10">
        <span className={iconClass}>{icon}</span>
      </div>
      <div className="text-base font-display font-black" style={{ color: accent }}>{value}</div>
      <div className="text-[10px] text-white/60 font-medium truncate" title={label}>{label}</div>
    </div>
  );
}

function Stat({ label, value, accent, testid }) {
  return (
    <div className="text-center px-1 min-w-0" data-testid={testid}>
      <div className="font-display font-black text-lg md:text-xl truncate" style={{ color: accent }}>{value}</div>
      <div className="text-[10px] uppercase font-bold tracking-widest text-white/40 mt-0.5 truncate">{label}</div>
    </div>
  );
}

function SocialRow({ platform, url, isEditing = false, onDraftChange, onEdit, onCancel, onSave }) {
  const { Icon, label, color, testid, kind = "url", placeholder = "" } = platform;
  const iconNode = <Icon size={16} color={color} />;
  const normalizedValue = kind === "text" ? String(url || "").replace(/^@+/, "") : url;

  if (isEditing) {
    return (
      <div className="w-full flex flex-col sm:flex-row items-stretch sm:items-center gap-2 bg-black/40 border border-white/15 rounded-xl p-2.5 text-xs" data-testid={`${testid}-edit`}>
        <div className="flex items-center gap-2">
          <span>{iconNode}</span>
          <span className="font-bold text-white">{label}</span>
        </div>
        <input
          value={normalizedValue}
          onChange={(e) => onDraftChange?.(e.target.value)}
          placeholder={kind === "text" ? placeholder : `https://${label.toLowerCase()}.com/username`}
          className="flex-1 min-w-0 bg-black/60 border border-white/15 rounded-lg px-3 py-1.5 outline-none focus:border-gold-500/50 text-white text-xs"
        />
        <div className="flex items-center gap-2 justify-end">
          <button
            type="button"
            onClick={onSave}
            className="px-3 py-1.5 rounded-lg bg-emerald-500 text-black font-black hover:bg-emerald-400 text-xs"
          >
            تم
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 rounded-lg border border-white/10 text-white/75 hover:bg-white/5 text-xs font-bold"
          >
            إلغاء
          </button>
        </div>
      </div>
    );
  }

  if (!url) {
    return (
      <div className="w-full flex items-center gap-3 bg-black/30 border border-white/5 rounded-xl px-3 py-2.5 text-xs text-white/40 overflow-hidden" data-testid={testid}>
        <span className="shrink-0">{iconNode}</span>
        <span className="font-bold shrink-0 text-white/60 w-16">{label}</span>
        <span className="text-[11px] flex-1 truncate">— غير مرتبط —</span>
        <button type="button" onClick={onEdit} className="p-1 rounded-lg border border-white/10 text-white/60 hover:bg-white/10 hover:text-white transition shrink-0">
          <Pencil size={12} />
        </button>
      </div>
    );
  }

  if (kind === "text") {
    return (
      <div data-testid={testid} className="w-full flex items-center gap-3 bg-black/30 border border-white/5 rounded-xl px-3 py-2.5 text-xs hover:border-white/15 hover:bg-black/50 transition overflow-hidden">
        <span className="shrink-0">{iconNode}</span>
        <span className="font-bold shrink-0 text-white w-16">{label}</span>
        <span className="text-white/80 truncate flex-1 font-mono min-w-0" dir="ltr">@{normalizedValue}</span>
        <button type="button" onClick={onEdit} className="p-1 rounded-lg border border-white/10 text-white/60 hover:bg-white/10 hover:text-white transition shrink-0">
          <Pencil size={12} />
        </button>
      </div>
    );
  }

  return (
    <div data-testid={testid} className="w-full flex items-center gap-3 bg-black/30 border border-white/5 rounded-xl px-3 py-2.5 text-xs hover:border-white/15 hover:bg-black/50 transition overflow-hidden">
      <span className="shrink-0">{iconNode}</span>
      <span className="font-bold shrink-0 text-white w-16">{label}</span>
      <a href={url} target="_blank" rel="noreferrer" className="text-white/70 truncate flex-1 hover:text-gold-400 transition min-w-0">{url}</a>
      <button type="button" onClick={onEdit} className="p-1 rounded-lg border border-white/10 text-white/60 hover:bg-white/10 hover:text-white transition shrink-0">
        <Pencil size={12} />
      </button>
    </div>
  );
}

async function fileToDataUrl(file) {
  return new Promise((res, rej) => {
    const fr = new FileReader();
    fr.onload = () => res(fr.result);
    fr.onerror = rej;
    fr.readAsDataURL(file);
  });
}

function MediaEditOverlay({ label = "تعديل", avatar = false }) {
  return (
    <div className={`absolute inset-0 grid place-items-center bg-black/50 backdrop-blur-[2px] opacity-0 group-hover/banner:opacity-100 group-hover/avatar:opacity-100 transition-opacity duration-200 ${avatar ? "rounded-full" : ""}`}>
      <span className={`inline-flex items-center gap-1.5 text-white ${avatar ? "text-[10px]" : "text-xs"} px-2.5 py-1 rounded-xl border border-white/20 bg-black/70 font-bold shadow-lg text-center`}>
        <FaPenToSquare size={11} /> {label}
      </span>
    </div>
  );
}

function SingleColorPicker({ value, onChange, disabled = false }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-black/30 p-4 shadow-inner" data-testid="single-color-editor">
      <div className="text-[10px] uppercase font-bold tracking-widest text-white/45 mb-2">لون البروفايل المميز (Accent Color)</div>
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl border border-white/20 shadow-md shrink-0" style={{ background: value }} />
        <input
          data-testid="profile-color-input"
          type="color"
          value={value}
          disabled={disabled}
          onChange={(e) => onChange?.(e.target.value)}
          className="h-10 w-20 cursor-pointer bg-transparent border-0 disabled:opacity-45"
        />
        <div className="text-xs text-white/75 font-mono">{value}</div>
      </div>
      {!disabled ? null : <div className="text-[10px] text-white/45 mt-2 font-bold leading-relaxed">⚠️ تغيير لون الملف الشخصي متاح لمشتركي Personal Plus فقط.</div>}
    </div>
  );
}

function ProfileDraftActionBar({ visible, saving, onSave, onDiscard }) {
  return (
    <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[999] transition-all duration-300 w-[92%] sm:w-auto max-w-md ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8 pointer-events-none"}`}>
      <div className="rounded-2xl border border-white/25 bg-[#141417]/95 backdrop-blur-xl px-4 sm:px-5 py-3.5 shadow-2xl flex flex-col sm:flex-row items-center gap-3">
        <span className="text-xs font-bold text-white/70 text-center sm:text-right">لديك تعديلات غير محفوظة:</span>
        <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
          <button
            type="button"
            disabled={saving}
            onClick={onSave}
            className="flex-1 sm:flex-initial px-4 py-2 rounded-xl bg-gold-500 text-black font-black hover:bg-gold-400 text-xs shadow-[0_0_15px_rgba(255,204,0,0.3)] disabled:opacity-50 transition text-center"
          >
            {saving ? "جاري الحفظ..." : "حفظ التغييرات"}
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={onDiscard}
            className="flex-1 sm:flex-initial px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-white/80 font-bold hover:bg-white/10 text-xs disabled:opacity-50 transition text-center"
          >
            تجاهل
          </button>
        </div>
      </div>
    </div>
  );
}
